
// rms-stepper/rms-stepper.component.ts
import {
  AfterContentInit, Component, ContentChildren, EventEmitter, HostListener,
  Input, Output, QueryList, computed, signal
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { RmsStepComponent } from './rms-step.component';
import { MatIconModule } from '@angular/material/icon';

type Orientation = 'horizontal' | 'vertical';

@Component({
  selector: 'rms-stepper',
  standalone: true,
  imports: [CommonModule, MatIconModule],
  styleUrls: ['./rms-stepper.component.scss'],
  templateUrl: './rms-stepper.component.html'
})
export class RmsStepperComponent implements AfterContentInit {
  @ContentChildren(RmsStepComponent) steps!: QueryList<RmsStepComponent>;

  /** Horizontal or vertical layout */
  @Input() orientation: Orientation = 'horizontal';

  /** Enforce valid previous steps before advancing */
  @Input() linear = false;

  /** Allow clicking headers to navigate when not linear */
  @Input() headerClickable = true;

  /** Show progress bar */
  @Input() showProgress = true;

  /** Button labels */
  @Input() nextText = 'Next';
  @Input() prevText = 'Back';
  @Input() finishText = 'Finish';

  /** Fieldset background (keeps parity with your inputs) */
  @Input() backgroundColor: string = 'white';

  /** Two-way-ish: index of the active step (ControlValueAccessor-like behavior) */
  @Input() set index(i: number) {
    if (Number.isInteger(i) && i >= 0) this._activeIndex.set(Math.min(i, this.stepList.length - 1));
  }
  get index() { return this._activeIndex(); }

  /** Emits on step index change */
  @Output() stepChange = new EventEmitter<number>();
  /** Emits when user hits finish */
  @Output() completed = new EventEmitter<void>();

  // internal signals
  private _activeIndex = signal(0);

  stepList: RmsStepComponent[] = [];

  ngAfterContentInit(): void {
    this.stepList = this.steps.toArray();
    // keep up to date if projected steps change
    this.steps.changes.subscribe(() => this.stepList = this.steps.toArray());
  }

  activeIndex = computed(() => this._activeIndex());

  isLast(): boolean {
    return this._activeIndex() === this.stepList.length - 1;
  }

  isComplete(i: number): boolean {
    const step = this.stepList[i];
    if (!step) return false;
    if (step.stepControl) return step.stepControl.valid === true;
    if (step.optional) return true;
    return step.completed === true;
  }

  isError(i: number): boolean {
    const step = this.stepList[i];
    if (!step) return false;
    if (!this.linear) return false; // only show invalid state in linear mode
    if (i > this._activeIndex()) return false;
    if (step.optional) return false;
    if (step.stepControl) return step.stepControl.invalid === true && (step.stepControl.touched || step.stepControl.dirty);
    return false;
  }

  canGoTo(i: number): boolean {
    if (i === this._activeIndex()) return true;
    const step = this.stepList[i];
    if (!step) return false;

    if (!this.linear) {
      // in non-linear, per-step editable can still block header click
      return this.headerClickable && step.editable;
    }
    // linear: ensure all previous steps are complete
    for (let j = 0; j < i; j++) {
      if (!this.isComplete(j)) return false;
    }
    return true;
  }

  currentCanAdvance(): boolean {
    const i = this._activeIndex();
    const step = this.stepList[i];
    if (!step) return false;
    if (!this.linear) return true;
    if (step.optional) return true;
    if (step.stepControl) return step.stepControl.valid === true;
    return step.completed === true; // manual completion toggle
  }

  allValid(): boolean {
    if (!this.linear) return true;
    return this.stepList.every((_, i) => this.isComplete(i));
  }

  onHeaderClick(i: number): void {
    if (!this.canGoTo(i)) return;
    this._activeIndex.set(i);
    this.stepChange.emit(i);
  }

  onHeaderKeydown(event: KeyboardEvent, i: number): void {
    const key = event.key;
    const last = this.stepList.length - 1;
    if (key === 'ArrowRight' || key === 'ArrowDown') {
      event.preventDefault();
      for (let k = i + 1; k <= last; k++) if (this.canGoTo(k)) { this.onHeaderClick(k); break; }
    } else if (key === 'ArrowLeft' || key === 'ArrowUp') {
      event.preventDefault();
      for (let k = i - 1; k >= 0; k--) if (this.canGoTo(k)) { this.onHeaderClick(k); break; }
    } else if (key === 'Home') {
      event.preventDefault();
      if (this.canGoTo(0)) this.onHeaderClick(0);
    } else if (key === 'End') {
      event.preventDefault();
      if (this.canGoTo(last)) this.onHeaderClick(last);
    } else if (key === 'Enter' || key === ' ') {
      event.preventDefault();
      this.onHeaderClick(i);
    }
  }

  next(): void {
    this.markCurrentTouchedIfInvalid();
    const i = this._activeIndex();
    if (i < this.stepList.length - 1 && this.canGoTo(i + 1)) {
      this._activeIndex.set(i + 1);
      this.stepChange.emit(i + 1);
    }
  }

  prev(): void {
    const i = this._activeIndex();
    if (i > 0) {
      this._activeIndex.set(i - 1);
      this.stepChange.emit(i - 1);
    }
  }

  finish(): void {
    this.markCurrentTouchedIfInvalid();
    if (!this.allValid()) return;
    this.completed.emit();
  }

  private markCurrentTouchedIfInvalid(): void {
    const current = this.stepList[this._activeIndex()];
    if (current?.stepControl && current.stepControl.invalid) {
      // mark controls touched for validation UI
      const ctrl = current.stepControl as any;
      if (typeof ctrl.markAllAsTouched === 'function') ctrl.markAllAsTouched();
      else if (typeof ctrl.markAsTouched === 'function') ctrl.markAsTouched();
    }
  }

  progressPct = computed(() => {
    if (!this.stepList.length) return 0;
    const done = this.stepList.filter((_, i) => this.isComplete(i)).length;
    return Math.round((done / this.stepList.length) * 100);
  });
}
